import React, { useRef, useState, useEffect } from 'react';
import { Button, Divider, Modal, Input, Select } from 'antd';
import { PlusCircleOutlined, MinusCircleOutlined, DeleteOutlined, DownOutlined, EditOutlined, ShoppingCartOutlined, SwapOutlined } from '@ant-design/icons';
import '../components/Cart.css';
import Swal from 'sweetalert2';

const Cart = ({ tableNumber, tableOrders, orderId, cartItems, setCartItems, increaseQuantity, decreaseQuantity, removeFromCart, onOrder, quantityAnimKey, setQuantityAnimKey, itemsData, showSlug, currencyCode, setTableNumber, allTableNumbers, settings }) => {
    const [expandedKey, setExpandedKey] = useState([]);

    const [isChangingTable, setIsChangingTable] = useState(false);
    
    const getItemTotal = (item) => {
        if (item.category === "Combo") {
            // For combo items: disable pricing for standard menu items, only allow custom option prices
            const customOptionsTotal = item.addOns
                ? item.addOns.reduce((sum, addon) => {
                    // Only add price if it's a custom option (has id from timestamp, not _id from database)
                    // Standard menu items (with _id) have their pricing disabled
                    const isCustomOption = addon.isCategoryItem ? false : (addon.id && !addon._id);
                    return isCustomOption ? sum + parseFloat(addon.price || 0) : sum;
                }, 0)
                : 0;
            
            return (item.price + customOptionsTotal) * item.quantity;
        }
        
        // For regular items, add all add-on prices
        const addOnsTotal = item.addOns
            ? item.addOns.reduce((sum, addon) => sum + parseFloat(addon.price || 0), 0)
            : 0;
        return (item.price + addOnsTotal) * item.quantity;
    };

    const subTotal = cartItems.reduce((acc, item) => acc + getItemTotal(item), 0);
    // Values from the image for display purposes
    const tax = subTotal > 0 ? (subTotal * 0.18) : 0; // Example tax
    const rounding = subTotal > 0 ? 0.02 : 0;
    const grandTotal = subTotal + tax + rounding;

    // ******* cart scroll arrow ******* //
    const cartListRef = useRef();
    const prevCartItemsRef = useRef(cartItems);
    const [showDownArrow, setShowDownArrow] = useState(false);
    const [highlightKey, setHighlightKey] = useState(null);
    const itemRefs = useRef({});

    useEffect(() => {
        const prev = prevCartItemsRef.current;
        // Scroll to the bottom when a new item is added and the new item is in the last item
        if (cartItems.length > prev.length) {
            const prevKeys = prev.map(x => x.cartKey);
            const newItem = cartItems.find(x => !prevKeys.includes(x.cartKey));
            if (newItem && cartItems[cartItems.length - 1].cartKey === newItem.cartKey) {
                if (cartListRef.current) {
                    cartListRef.current.scrollTop = cartListRef.current.scrollHeight;
                }
                setHighlightKey(newItem.cartKey);
                setTimeout(() => setHighlightKey(null), 800);
            }
        } else if (cartItems.length === prev.length) {
            // When the number of existing products increases, scroll to the existing product position
            for (let i = 0; i < cartItems.length; i++) {
                const prevItem = prev[i];
                const currItem = cartItems[i];
                if (
                    prevItem &&
                    currItem &&
                    prevItem.cartKey === currItem.cartKey &&
                    currItem.quantity > prevItem.quantity
                ) {
                    const ref = itemRefs.current[currItem.cartKey];
                    if (ref && ref.scrollIntoView) {
                        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                    setHighlightKey(currItem.cartKey);
                    setTimeout(() => setHighlightKey(null), 800);
                }
            }
        }
        prevCartItemsRef.current = cartItems;

        // showDownArrow
        const el = cartListRef.current;
        if (!el) return;
        const checkOverflow = () => {
            setShowDownArrow(el.scrollHeight > el.clientHeight + 2 && el.scrollTop + el.clientHeight < el.scrollHeight - 2);
        };
        checkOverflow();
        el.addEventListener('scroll', checkOverflow);
        window.addEventListener('resize', checkOverflow);
        return () => {
            el.removeEventListener('scroll', checkOverflow);
            window.removeEventListener('resize', checkOverflow);
        };
    }, [cartItems]);

    // ******* edit cart ******* //
    const [expandedOrderOid, setExpandedOrderOid] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [editQuantity, setEditQuantity] = useState(1);
    const [editRemark, setEditRemark] = useState('');
    const [editAddOns, setEditAddOns] = useState([]); // [{group, id, name, price}]
    const [editSubMenu, setEditSubMenu] = useState([]);

    const handleEditItem = (item) => {
        setEditingItem(item);
        setEditQuantity(item.quantity || 1);
        setEditRemark(item.remarks || '');
        setEditAddOns(item.addOns || []);
        // find item submenu
        const product = itemsData.find(p => p._id === item._id);
        setEditSubMenu(product?.subMenu || []);
    };

    // save edit modal
    const handleSaveEdit = async () => {
        // Validate required submenus
        const missingRequiredGroups = editSubMenu
            .filter(submenu => submenu.requirement === 'required')
            .filter(submenu => !editAddOns.some(a => a.group === submenu.group));

        if (missingRequiredGroups.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Required Selection',
                text: `Please select at least one option for: ${missingRequiredGroups.map(g => g.group).join(', ')}`,
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            return; // Stop save
        }

        try {
            setCartItems(prev =>
                prev.map(x =>
                    x.cartKey === editingItem.cartKey
                        ? { ...x, quantity: editQuantity, remarks: editRemark, addOns: editAddOns }
                        : x
                )
            );
            setEditingItem(null);
            Swal.fire({
                title: 'Success',
                text: 'Item updated successfully',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update item',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
    };


    // addons
    const handleAddonChange = (group, item, checked, isMultiple) => {
        if (isMultiple) {
            if (checked) {
                setEditAddOns(prev => [...prev, { ...item, group }]);
            } else {
                setEditAddOns(prev => prev.filter(a => !(a.group === group && a.id === item.id)));
            }
        } else {
            setEditAddOns(prev => [
                ...prev.filter(a => a.group !== group),
                { ...item, group }
            ]);
        }
    };

    const validOrders = Array.isArray(tableOrders)
        ? tableOrders.filter(order =>
            String(order.order_tablenumber).trim() === String(tableNumber).trim() &&
            Array.isArray(order.order_items) &&
            order.order_items.length > 0 &&
            Number(order.total) > 0
        )
        : [];

    // console.log('tableNumber:', tableNumber, 'tableOrders:', tableOrders, 'validOrders:', validOrders);

    return (
        <div className="cart-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* cart header */}
            <div className="cart-header">
                {!tableNumber || isChangingTable  ? (
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 8, fontSize: 22, fontWeight: 600 }}>Table:</span>
                        <Select
                            style={{ width: 120, fontSize: 20 }}
                            placeholder="Table"
                            value={tableNumber || undefined}
                            onChange={num => { setTableNumber(num); setIsChangingTable(false); }}
                            options={allTableNumbers.map(t => ({
                                value: t.value,
                                label: (
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                    <span
                                    style={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: t.status === 'available' ? '#52c41a' : '#ef4444',
                                        marginRight: 8
                                    }}
                                    />
                                    <span style={{ marginRight: 8 }}>{t.value}</span>
                                </span>
                                )
                            }))}
                            autoFocus
                        />
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                        <h3 style={{ marginRight: 8, marginBottom: 0 }}>Table {tableNumber}</h3>
                        <SwapOutlined
                            style={{ fontSize: 20, color: '#ff7a00', cursor: 'pointer' }}
                            onClick={() => setIsChangingTable(true)}
                            title="Change table"
                        />
                    </div>
                )}
                {settings && settings.isAccessKeyNeeded && (
                    <span>User: {localStorage.getItem('userId') || '--'}</span>
                )}
            </div>

            {/* history order button */}
            {validOrders.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', margin: '8px 0 0 0' }}>
                    <span style={{ fontWeight: 600, fontSize: 15, marginRight: 8 }}>Orders:</span>
                    {/* ALL */}
                    {validOrders.length > 1 && (
                        <div
                            title="All Orders"
                            style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                background: expandedOrderOid === 'ALL' ? '#ff7a00' : '#fff',
                                border: '2px solid #ff7a00',
                                marginRight: 10,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontWeight: 700,
                                color: expandedOrderOid === 'ALL' ? '#fff' : '#ff7a00',
                                fontSize: 11,
                                transition: 'background 0.2s',
                            }}
                            onClick={() => setExpandedOrderOid(expandedOrderOid === 'ALL' ? null : 'ALL')}
                        >
                            ALL
                        </div>
                    )}
                    {/* order bullet */}
                    {validOrders.map(order => (
                        <div
                            key={order.oid}
                            title={order.oid}
                            style={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: expandedOrderOid === order.oid ? '#ff7a00' : '#fff',
                                border: '2px solid #ff7a00',
                                marginRight: 10,
                                display: 'inline-block',
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                                position: 'relative',
                            }}
                            onClick={() => setExpandedOrderOid(expandedOrderOid === order.oid ? null : order.oid)}
                        >
                            {expandedOrderOid === order.oid && (
                                <span style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    width: 10,
                                    height: 10,
                                    background: '#fff',
                                    borderRadius: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents: 'none'
                                }} />
                            )}
                        </div>
                    ))}
                    
                </div>
                
                
            )}
            <Divider className="cart-divider" />

            {/* cart list */}
            <div className="cart-items-list cart-scroll-hide" ref={cartListRef}>
                {/* show all order items */}
                {expandedOrderOid === 'ALL' && (() => {
                    const allItems = [];
                    validOrders.forEach(order => {
                        order.order_items.forEach(item => {
                            const key = (item.product?.name || item.name) + '_' +
                                JSON.stringify(item.product?.addOns || item.addOns || []) + '_' +
                                (item.remarks || '');
                            const exist = allItems.find(i => i.key === key);
                            if (exist) {
                                exist.quantity += item.quantity;
                            } else {
                                allItems.push({
                                    key,
                                    name: item.product?.name || item.name,
                                    slug: item.product?.slug || item.slug,
                                    quantity: item.quantity,
                                    remarks: item.remarks,
                                    addOns: item.product?.addOns || item.addOns || [],
                                });
                            }
                        });
                    });
                    if (allItems.length === 0) return null;
                    return (
                        <div style={{ marginBottom: 16, background: '#f6f6f6', borderRadius: 8, padding: 8 }}>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6, color: '#ff7a00' }}>
                                All
                            </div>
                            {allItems.map((item, idx) => (
                                <div
                                    key={item.key}
                                    className="cart-item"
                                    style={{
                                        opacity: 0.7,
                                        pointerEvents: 'none',
                                        background: '#fff',
                                        border: '1px solid #eee',
                                        borderRadius: 6,
                                        marginBottom: 6,
                                        padding: 8
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ fontWeight: 500, fontSize: 15 }}>
                                            {idx + 1}. {showSlug ? item.slug : item.name}
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                                            × {item.quantity}
                                        </div>
                                    </div>
                                    {item.remarks && (
                                        <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>Remark: {item.remarks}</div>
                                    )}
                                    {item.addOns && item.addOns.length > 0 && (
                                        <ul style={{ margin: 0, paddingLeft: 18, color: '#888', fontSize: 13 }}>
                                            {item.addOns.map((addon, i) => (
                                                <li key={i}>{addon.group}: {addon.name} (+ {currencyCode} {parseFloat(addon.price || 0).toFixed(2)})
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })()}
                {/* show single order items */}
                {expandedOrderOid && expandedOrderOid !== 'ALL' && (() => {
                    const order = validOrders.find(o => o.oid === expandedOrderOid);
                    if (!order) return null;
                    return (
                        <div style={{ marginBottom: 16, background: '#f6f6f6', borderRadius: 8, padding: 8 }}>
                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6, color: '#ff7a00' }}>
                                Order #{order.oid}
                            </div>
                            {order.order_items.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="cart-item"
                                    style={{
                                        opacity: 0.7,
                                        pointerEvents: 'none',
                                        background: '#fff',
                                        border: '1px solid #eee',
                                        borderRadius: 6,
                                        marginBottom: 6,
                                        padding: 8
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ fontWeight: 500, fontSize: 15 }}>
                                            {idx + 1}. {showSlug ? (item.product?.slug || item.slug) : (item.product?.name || item.name)}
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: 15 }}>
                                            × {item.quantity}
                                        </div>
                                    </div>
                                    {item.remarks && (
                                        <div style={{ color: '#888', fontSize: 13, marginTop: 2 }}>Remark: {item.remarks}</div>
                                    )}
                                    {item.product?.addOns && item.product.addOns.length > 0 && (
                                        <ul style={{ margin: 0, paddingLeft: 18, color: '#888', fontSize: 13 }}>
                                            {item.product.addOns.map((addon, i) => (
                                                <li key={i}>{addon.group}: {addon.name} (+MYR {addon.price})</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {cartItems.length === 0 ? (
                    <div className="cart-empty-center">
                    <ShoppingCartOutlined style={{ fontSize: 48, marginBottom: 16, color: '#ff7a00' }} />
                        <div style={{ textAlign: 'center', lineHeight: 1.6, color: '#aaa', fontSize: 17 }}>
                            Your cart is empty.<br />
                            Please add items to the cart before placing an order.
                        </div>
                    </div>
                ) : (
                    cartItems.map((item, idx) => {
                        const hasExtra = (item.remarks && item.remarks.length > 0) || (item.addOns && item.addOns.length > 0);
                        const isExpanded = expandedKey.includes(item.cartKey);
                        const itemTotal = getItemTotal(item);

                        return (
                            <div key={item.cartKey} ref={el => itemRefs.current[item.cartKey] = el} className={`cart-item${highlightKey === item.cartKey ? ' cart-item-highlight' : ''}`}>
                                {/* First row: item name and price */}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        cursor: hasExtra ? 'pointer' : 'default'
                                    }}
                                    onClick={() => {
                                        if (!hasExtra) return;

                                        setExpandedKey((prevKeys) => {
                                            if (prevKeys.includes(item.cartKey)) {
                                                return prevKeys.filter((key) => key !== item.cartKey);
                                            } else {
                                                return [...prevKeys, item.cartKey];
                                            }
                                        });
                                    }}                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        fontWeight: 600,
                                        flex: 1,
                                        minWidth: 0,
                                        marginRight: 8,
                                        maxWidth: '70%'
                                    }}>
                                        <span style={{ marginRight: 8 }}>{idx + 1}.</span>
                                        <span
                                            style={{
                                                wordBreak: 'break-all',
                                                whiteSpace: 'normal',
                                                display: 'block',
                                            }}
                                        >
                                            {showSlug ? item.slug : item.name}
                                        </span>
                                        {hasExtra && (
                                            <DownOutlined style={{ marginLeft: 6, color: '#888', fontSize: 12, alignSelf: 'flex-start', lineHeight: '2.2' }} />
                                        )}
                                        <EditOutlined
                                            className="edit-item-btn"
                                            style={{ marginLeft: 8, color: '#ff7a00', cursor: 'pointer' }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleEditItem(item);
                                            }}
                                        />
                                    </div>

                                    <div
                                        style={{
                                            fontWeight: 600,
                                            marginLeft: 8,
                                            minWidth: 70,
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {currencyCode} {itemTotal.toFixed(2)}
                                    </div>
                                </div>

                                {/* Second row: Remarks and additional items (shown when expanded) */}
                                {isExpanded && (
                                    <div style={{ marginTop: 4 }}>
                                        {item.remarks && (
                                            <div style={{ color: '#888', wordBreak: 'break-all', fontSize: 13 }}>Remark: {item.remarks}</div>
                                        )}
                                        {item.addOns && item.addOns.length > 0 && (
                                            <div style={{ color: '#888', fontSize: 13 }}>
                                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                    {item.addOns.map((addon, idx) => (
                                                        <li key={idx}>
                                                            {addon.group}: {addon.name} ( + {currencyCode} {parseFloat(addon.price || 0).toFixed(2)})
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Third row: action button (lower right corner) */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: 8
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <MinusCircleOutlined
                                            className="quantity-btn"
                                            onClick={e => { e.stopPropagation(); decreaseQuantity(item); }}
                                        />
                                        <span
                                            key={item.cartKey + '-' + item.quantity}
                                            className={quantityAnimKey && quantityAnimKey.startsWith(item.cartKey) ? "item-quantity quantity-animate" : "item-quantity"}
                                            style={{ margin: '0 8px' }}
                                            onAnimationEnd={() => setQuantityAnimKey(null)}
                                        >
                                            {item.quantity}
                                        </span>
                                        <PlusCircleOutlined
                                            className="quantity-btn"
                                            onClick={e => { e.stopPropagation(); increaseQuantity(item); }}
                                        />
                                        <DeleteOutlined
                                            className="remove-item-btn"
                                            style={{ marginLeft: 12 }}
                                            onClick={e => { 
                                                e.stopPropagation(); 
                                                removeFromCart(item); 
                                                Swal.fire({
                                                    icon: 'success',
                                                    title: 'Deleted',
                                                    toast: true,
                                                    position: 'top-end',
                                                    showConfirmButton: false,
                                                    timer: 2000
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            {showDownArrow && (
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 109,
                        height: 40,
                        pointerEvents: 'auto',
                        background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, #fff 80%)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        zIndex: 10
                    }}
                    onClick={() => {
                        if (cartListRef.current) {
                            // scroll to bottom V
                            // cartListRef.current.scrollTo({ top: cartListRef.current.scrollHeight, behavior: 'smooth' });

                            // scroll half V
                            const el = cartListRef.current;
                            el.scrollBy({ top: el.clientHeight, behavior: 'smooth' });
                        }
                    }}
                >
                    <DownOutlined className="down-arrow-animated" style={{ fontSize: 24, color: '#ff7a00', background: '#fff', borderRadius: '50%' }} />
                </div>
            )}

            <div className="cart-summary">
                {/* <div className="summary-row">
                    <span>Total</span>
                    <span>RM {subTotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                    <span>Tax</span>
                    <span>RM {tax.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                    <span>Rounding</span>
                    <span>RM {rounding.toFixed(2)}</span>
                </div> 
                <Divider style={{ margin: '10px 0' }} />
                <div className="summary-row grand-total">
                    <h4>Grand Total</h4>
                    <h4>RM {grandTotal.toFixed(2)}</h4>
                </div>*/}
                <div className="summary-row total">
                    <h4>Total</h4>
                    <h4>{currencyCode} {subTotal.toFixed(2)}</h4>
                </div>
            </div>

            {/* <div className="cart-actions">
                <div className="cart-actions-btns">
                    <Button>Void</Button>
                    <Button>% Disc.</Button>
                    <Button>- Tax</Button>
                    <Button>Deposit</Button>
                    <Button>Save</Button>
                    <Button>Save & Send</Button>
                    <Button>Print</Button>
                    <Button>QR</Button>
                </div>
            </div> */}

            <Button type="primary" className="cart-payment-button mt-3" block onClick={onOrder} disabled={cartItems.length === 0}>
                Order Now
            </Button>

            {/* edit modal */}
            <Modal
                className="cart-edit-modal"
                open={!!editingItem}
                title={<span style={{ fontSize: 20, fontWeight: 600 }}>Edit Item</span>}
                onCancel={() => setEditingItem(null)}
                onOk={handleSaveEdit}
                okText="Save"
                cancelText="Cancel"
            >
                <div style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 12, fontSize: 18 }}>
                        Item Name: <span style={{ fontWeight: 500 }}>{showSlug ? editingItem?.slug : editingItem?.name}</span>
                    </div>
                    <div style={{ marginBottom: 12, fontSize: 16 }}>
                        Quantity:
                        <Input
                            type="number"
                            min={1}
                            value={editQuantity}
                            onChange={e => setEditQuantity(Number(e.target.value) || 1)}
                            style={{ width: 50, marginLeft: 8 }}
                        />
                    </div>
                    <div style={{ marginBottom: 12, fontSize: 16 }}>
                        Remark:
                        <Input.TextArea
                            value={editRemark}
                            onChange={e => setEditRemark(e.target.value)}
                            placeholder="Remark"
                            rows={2}
                            style={{ wordBreak: 'break-all' }}
                        />
                    </div>

                    {/* submenu */}
                    <div style={{ marginTop: 25 }}>
                        <div style={{ fontWeight: 500, fontSize: 17, marginBottom: 8 }}>Sub Menu：</div>
                        {editSubMenu.length === 0 ? (
                            <div style={{ color: '#888', fontSize: 15, fontWeight: 400, marginBottom: 8 }}>
                                No submenus available for this item.
                            </div>
                        ) : (
                          editSubMenu
                            .slice() // clone to avoid mutating the original
                            .sort((a, b) => (a.selection === 'compulsory' ? -1 : 1))
                            .map((submenu, idx) => {
                                const isCompulsory = submenu.selection === 'compulsory';
                                const isRequired = submenu.requirement === 'required';

                                // check if any item from this group is selected
                                const hasSelection = editAddOns.some(a => a.group === submenu.group);

                                return (
                                    <div key={submenu.id || idx} style={{ marginBottom: 12 }}>
                                        <div style={{ fontWeight: 400, fontSize: 15, marginBottom: 4 }}>
                                            {submenu.group}
                                            {isRequired && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                                        </div>

                                        {isRequired && !hasSelection && !isCompulsory && (
                                            <div style={{ color: 'red', fontSize: 13, marginBottom: 6 }}>
                                                Please select at least one option from {submenu.group}.
                                            </div>
                                        )}

                                        <div style={{ fontSize: 15 }}>
                                            {submenu.selection === 'single' ? (
                                                submenu.items.map(item => {
                                                    const checked = isCompulsory
                                                        ? true
                                                        : editAddOns.some(a => a.group === submenu.group && a.id === item.id);
                                                    const disabled = isCompulsory;

                                                    return (
                                                        <label key={item.id} style={{ marginRight: 20, fontSize: 16 }}>
                                                            <input
                                                                type="radio"
                                                                name={`addon-${submenu.group}`}
                                                                checked={checked}
                                                                disabled={disabled}
                                                                onChange={e =>
                                                                    !isCompulsory &&
                                                                    handleAddonChange(submenu.group, item, e.target.checked, false)
                                                                }
                                                                style={{ marginRight: 4 }}
                                                            />
                                                            {item.name} (+{currencyCode} {parseFloat(item.price).toFixed(2)})
                                                        </label>
                                                    );
                                                })
                                            ) : (
                                                submenu.items.map(item => {
                                                    const checked = isCompulsory
                                                        ? true
                                                        : editAddOns.some(a => a.group === submenu.group && a.id === item.id);
                                                    const disabled = isCompulsory;

                                                    return (
                                                        <label key={item.id} style={{ marginRight: 20, fontSize: 16 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                disabled={disabled}
                                                                onChange={e =>
                                                                    !isCompulsory &&
                                                                    handleAddonChange(submenu.group, item, e.target.checked, true)
                                                                }
                                                                style={{ marginRight: 4 }}
                                                            />
                                                            {item.name} (+{currencyCode} {parseFloat(item.price).toFixed(2)})
                                                        </label>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Cart; 